import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEngine } from '../api/context';
import { useApiQuery, useCreate, useRemove } from '../api/hooks';
import { Button } from '../design/primitives/Button';
import { Switch } from '../design/primitives/Input';
import { Avatar, EmptyState, ErrorBanner, Skeleton } from '../design/primitives/Misc';
import { useConfirm } from '../design/primitives/Overlay';
import { RichTextEditor, RichTextView } from '../fields/RichText';
import { formatDateTime } from '../../lib/format';

// NotesPanel: /v1/notes?entity_type=&entity_id= — rich-text notes, internal/public flag,
// author + time, delete with confirm. Composer at the top so the newest is one keystroke away.

export interface Note {
  id: string;
  entity_type: string;
  entity_id: string;
  body: string;
  is_internal: boolean;
  author_user_id?: string | null;
  author_name?: string | null;
  created_at: string;
}

export function NotesPanel({ entityType, entityId }: { entityType: string; entityId: string }) {
  const qc = useQueryClient();
  const { principal } = useEngine();
  const confirm = useConfirm();
  const key = ['notes', entityType, entityId];
  const q = useApiQuery<{ data: Note[] }>(key, '/v1/notes', { query: { entity_type: entityType, entity_id: entityId, sort: '-created_at' } });
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(true);
  const [draftKey, setDraftKey] = useState(0);

  const create = useCreate<Note, Partial<Note>>('notes', {
    toast: { success: 'Note added' },
    onSuccess: () => {
      setBody('');
      setDraftKey((k) => k + 1);
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  const remove = useRemove('notes', { toast: { success: 'Note deleted' }, onSuccess: () => void qc.invalidateQueries({ queryKey: key }) });

  const rows = q.data?.data ?? [];
  const canPost = body.replace(/<[^>]*>/g, '').trim().length > 0;

  return (
    <div className="hf-notes">
      <form
        className="hf-notes-compose"
        onSubmit={(e) => {
          e.preventDefault();
          if (canPost) create.mutate({ entity_type: entityType, entity_id: entityId, body, is_internal: internal });
        }}
      >
        <RichTextEditor key={draftKey} value={body} onChange={setBody} placeholder="Write a note… (⌘↵ to post)" aria-label="New note" />
        <div className="hf-between" style={{ marginTop: 8 }}>
          <Switch checked={internal} onChange={(e) => setInternal(e.target.checked)} label={internal ? 'Internal' : 'Visible to client'} />
          <Button type="submit" variant="primary" size="sm" icon="send" disabled={!canPost} busy={create.isPending}>
            Post
          </Button>
        </div>
      </form>
      {q.isLoading ? (
        <Skeleton lines={3} />
      ) : q.error ? (
        <ErrorBanner error={q.error} />
      ) : rows.length === 0 ? (
        <EmptyState title="No notes" hint="Notes keep context with the record." icon="sticky-note" />
      ) : (
        <ol className="hf-notes-list">
          {rows.map((n) => (
            <li key={n.id} className={['hf-note', !n.is_internal && 'public'].filter(Boolean).join(' ')}>
              <div className="head">
                <Avatar name={n.author_name ?? n.author_user_id ?? '?'} size="sm" />
                <b>{n.author_name ?? n.author_user_id?.slice(0, 8) ?? 'Unknown'}</b>
                <time className="hf-muted hf-num" dateTime={n.created_at}>
                  {formatDateTime(n.created_at)}
                </time>
                {!n.is_internal && <span className="hf-badge info nodot">client-visible</span>}
                {(principal.id == null || principal.id === n.author_user_id) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="trash"
                    aria-label="Delete note"
                    style={{ marginLeft: 'auto' }}
                    onClick={async () => {
                      if ((await confirm({ title: 'Delete this note?', body: 'The note is removed from the record. This cannot be undone.', confirmLabel: 'Delete', danger: true })) !== false) remove.mutate(n.id);
                    }}
                  />
                )}
              </div>
              <RichTextView html={n.body} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
